import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userAtom } from '../../atoms/authAtoms';
import { getCurrentUser, logoutUser } from '../../login/controller/login-controller';
import { fetchClientsWithMultipleOrders } from './notification-controller/notification-controller';
import {
  AllProductsIcon, TaxIcon, SuppliersIcon, DashboardIcon, ProductsIcon,
  ChevronDownIcon, ChevronRightIcon, VariantIcon, BrandIcon, OrdersIcon,
  UsersIcon, SettingsIcon, ExchangeIcon, PurchasesIcon, ImportIcon, PromoCodeIcon, RouletteIcon
} from './icons/icons';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';
import { isAdmin } from '../../login/controller/login-controller';
import { FaFacebook } from 'react-icons/fa';

// Import our new components
import Sidebar from '../../components/adminLayout/Sidebar';
import NotificationSystem from '../../components/adminLayout/NotificationSystem';
import TopBar from '../../components/adminLayout/TopBar';
import MobileMenu from '../../components/adminLayout/MobileMenu';
import UserProfile from '../../components/adminLayout/UserProfile';

const scrollbarStyles = {
  // For Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(28, 86, 81, 0.5) transparent',

  // The rest will be handled by the className with style objects below
};

const AdminLayout = () => {
  // Existing state
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isProductsExpanded, setIsProductsExpanded] = useState(false);
  const [isOrdersExpanded, setIsOrdersExpanded] = useState(false);
  const [isStockExpanded, setIsStockExpanded] = useState(false);
  const [isCaisseExpanded, setIsCaisseExpanded] = useState(false);
  const [isAchatsExpanded, setIsAchatsExpanded] = useState(false);
  
  // Add notification related state
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs for notifications dropdown and previous notifications
  const notificationsRef = useRef(null);
  const prevNotificationsRef = useRef([]);

  const location = useLocation();
  const navigate = useNavigate();
  const { permission,hasPermission } = usePermissionViewModel();
const isAuth= isAdmin()

  // Load user data on component mount
  useEffect(() => {
    const userData = getCurrentUser();
    setUser(userData);
  }, []);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      try {
        const data = await fetchClientsWithMultipleOrders();
        
        // Process the notifications to add additional metadata
        const processedNotifications = data.map((notification, index) => {
          // Create a unique ID based on number + date + count
          const uniqueId = `${notification.numero}-${notification.dateBonCommande}-${notification.count}`;
          
          return {
            ...notification,
            id: uniqueId, // Use unique ID instead of just index
            read: false,  // Default to unread
            timestamp: new Date(notification.dateBonCommande).getTime(),
            formattedDate: formatNotificationDate(notification.dateBonCommande)
          };
        });
        
        // Sort by date (newest first)
        const sortedNotifications = processedNotifications.sort((a, b) => b.timestamp - a.timestamp);
        
        // Load read notification IDs from local storage
        const storedReadIds = localStorage.getItem('readNotifications');
        const readIds = storedReadIds ? JSON.parse(storedReadIds) : [];
        
        // Mark notifications as read if they're in the readIds
        const markedNotifications = sortedNotifications.map(notification => ({
          ...notification,
          read: readIds.includes(notification.id)
        }));
        
        // Check for new notifications by comparing with previous set
        if (prevNotificationsRef.current.length > 0) {
          // Find notifications that weren't in the previous set
          const prevIds = prevNotificationsRef.current.map(n => n.id);
          markedNotifications.forEach(notification => {
            if (!prevIds.includes(notification.id)) {
              notification.read = false; // Mark as unread if it's new
            }
          });
        }
        
        // Update the reference for next comparison
        prevNotificationsRef.current = markedNotifications;
        
        // Calculate unread count
        const unreadCount = markedNotifications.filter(n => !n.read).length;
        
        setNotifications(markedNotifications);
        setUnreadCount(unreadCount);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError('Failed to load notifications');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
    
    // Refresh notifications every 5 minutes
    const intervalId = setInterval(fetchNotifications, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Format notification date to a more user-friendly format
  const formatNotificationDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Same day
    if (date.toDateString() === now.toDateString()) {
      return "Aujourd'hui";
    }
    
    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Hier";
    }
    
    // Format date as DD/MM/YYYY for older dates
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mark all notifications as read
  const markAllAsRead = () => {
    // Get all notification IDs
    const allIds = notifications.map(n => n.id);
    
    // Update state
    setNotifications(current => 
      current.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
    
    // Save to local storage
    try {
      localStorage.setItem('readNotifications', JSON.stringify(allIds));
    } catch (error) {
      console.error('Error saving read status to storage', error);
    }
  };

  // Mark a single notification as read
  const markAsRead = (notificationId) => {
    // Update the notifications state
    setNotifications(prevNotifications => {
      // Find the notification
      const notificationIndex = prevNotifications.findIndex(n => n.id === notificationId);
      
      // If not found or already read, return unchanged notifications
      if (notificationIndex === -1 || prevNotifications[notificationIndex].read) {
        return prevNotifications;
      }
      
      // Create a new array with the updated notification
      const updatedNotifications = [...prevNotifications];
      updatedNotifications[notificationIndex] = {
        ...updatedNotifications[notificationIndex],
        read: true
      };
      
      // Update unread count
      setUnreadCount(prev => prev - 1);
      
      // Update localStorage with new read IDs
      const storedReadIds = localStorage.getItem('readNotifications');
      const readIds = storedReadIds ? JSON.parse(storedReadIds) : [];
      if (!readIds.includes(notificationId)) {
        const updatedReadIds = [...readIds, notificationId];
        localStorage.setItem('readNotifications', JSON.stringify(updatedReadIds));
      }
      
      return updatedNotifications;
    });
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Only mark as read if it's not already read
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Close the notifications dropdown
    setIsNotificationsOpen(false);
    
        // Calculate start and end dates for filtering
    const notifDateObj = new Date(notification.dateBonCommande);
    const startDateObj = new Date(notifDateObj.getFullYear(), notifDateObj.getMonth(), 1);
    const startDateStr = startDateObj.toISOString().slice(0, 10);
    const endDateStr = notifDateObj.toISOString().slice(0, 10);

    navigate('/admin/orders', { 
      state: { 
        fromNotification: true,
        filterPhoneNumber: notification.numero,
        filterStartDate: startDateStr,
        filterEndDate: endDateStr
      } 
    });
  };

  // Add a helper function to format phone numbers
  const formatPhoneNumber = (phoneNumber) => {
    // Convert scientific notation to regular number if needed
    let normalizedNumber = phoneNumber;
    if (phoneNumber.includes('e+')) {
      normalizedNumber = Number(phoneNumber).toFixed(0);
    }
    
    // Format: XX XXX XXX
    const cleaned = normalizedNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{3})(\d{3})$/);
    
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }
    
    return phoneNumber;
  };



  const handleProductsClick = (e) => {
    e.preventDefault();
    setIsProductsExpanded(!isProductsExpanded);
  };

  const handleOrdersClick = (e) => {
    e.preventDefault();
    setIsOrdersExpanded(!isOrdersExpanded);
  };

  const handleStockClick = (e) => {
    e.preventDefault();
    setIsStockExpanded(!isStockExpanded);
  };

  const handleCaisseClick = (e) => {
    e.preventDefault();
    setIsCaisseExpanded(!isCaisseExpanded);
  };

  const handleAchatsClick = (e) => {
    e.preventDefault();
    setIsAchatsExpanded(!isAchatsExpanded);
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const getUserAvatar = () => {
    if (user && user.image) {
      return user.image;
    }
    // Generate a default avatar with user's initials if available
    if (user && (user.firstName || user.lastName)) {
      const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`;
      return `https://ui-avatars.com/api/?name=${initials}&background=174540&color=fff&size=128`;
    }
    // Fallback to default image
    return "https://ui-avatars.com/api/?name=User&background=174540&color=fff&size=128";
  };

  function filterNavigationByPermissions(navigation) {
    // const hasReadPermission = (menuName) =>
    //   permissions.some((perm) => perm === `${menuName}_read`);
  
    return navigation
      .map((item) => {
        // Hide admin-only items for non-admin users
        if (item.adminOnly) return null;

        // If it has subItems, filter them first
        if (item.hasDropdown && item.subItems) {
          const filteredSubItems = item.subItems.filter((subItem) =>
         //   hasReadPermission(subItem.permissionName)
          (hasPermission('read',subItem.permissionName) || subItem.external|| hasPermission('write',subItem.permissionName))
          );
  
          if (filteredSubItems.length === 0) {
            return null; // No subitems, exclude parent
          }
  
          return {
            ...item,
            subItems: filteredSubItems,
          };
        }
  
        // For single-level items (no dropdown)
        return          (hasPermission( 'read',item.permissionName)||hasPermission( 'write',item.permissionName) )? item : null

       //  hasReadPermission(item.permissionName) ? item : null;
      })
      .filter(Boolean); // Remove null entries
  }
  

  // Update the navigation array to include the barcode verification link
  const navigation = [
    { name: 'Tableau de bord', href: '/admin', icon: DashboardIcon,permissionName: 'tableau_de_board' },
    {
      name: 'Produits',
      href: '#',
      icon: ProductsIcon,
      hasDropdown: true,
      subItems: [
        { name: 'Tous les produits', href: '/admin/products', icon: AllProductsIcon,     permissionName: 'produit' },
        { name: 'Variantes', href: '/admin/variants', icon: VariantIcon,     permissionName: 'variante', },
        { name: 'Marques', href: '/admin/brands', icon: BrandIcon, permissionName: 'marque'  },
        { name: 'Fournisseurs', href: '/admin/suppliers', icon: SuppliersIcon,permissionName: 'fournisseur' },
      ],
    },
    {
      name: 'Commandes',
      href: '#',
      icon: OrdersIcon,
      hasDropdown: true,
      subItems: [
        { name: 'Toutes les commandes', href: '/admin/orders', icon: OrdersIcon,permissionName: 'commande' },
        { name: 'Platforme', href: '/admin/platform', icon: DashboardIcon , permissionName: 'platforme'},
        { name: 'Échange', href: '/admin/exchanges', icon: ExchangeIcon, permissionName: 'echange' },
        { name: 'Charges', href: '/admin/charges', icon: PurchasesIcon, permissionName: 'charges' },
        { name: 'Retour Reçu', href: '/admin/retour-recu', icon: ExchangeIcon, permissionName: 'retour_recu' },
        { name: 'Vérification Code-Barres', href: '/barcode-verification', icon: ProductsIcon, external: true },
      ],
    },
    {
      name: 'Achats',
      href: '#',
      icon: PurchasesIcon,
      hasDropdown: true,
      subItems: [
        { name: 'Liste des achats', href: '/admin/purchases', icon: PurchasesIcon, permissionName: 'achat' },
        { name: 'Règlement Fournisseur', href: '/admin/reglement-fournisseur', icon: PurchasesIcon, permissionName: 'reglement_fournisseur' },
        { name: 'Consommation Fournisseur', href: '/admin/consommation-fournisseur', icon: PurchasesIcon, permissionName: 'consommation_fournisseur' },
      ],
    },
    {
      name: 'Caisse',
      href: '#',
      icon: PurchasesIcon,
      hasDropdown: true,
      subItems: [
        { name: 'Caisse', href: '/admin/caisse', icon: PurchasesIcon, permissionName: 'caisse' },
        { name: 'Liste des caisses', href: '/admin/caisses', icon: PurchasesIcon, permissionName: 'liste_caisses' },
        { name: 'Chiffre d’affaires', href: '/admin/caisses/chiffre-affaires', icon: DashboardIcon, permissionName: 'chiffre_affaires_caisse' },
      ],
    },
    {
      name: 'Stock',
      href: '#',
      icon: AllProductsIcon,
      hasDropdown: true,
      subItems: [
        { name: 'Liste Stock', href: '/admin/stocks', icon: AllProductsIcon, permissionName: 'stock' },
        { name: 'Dépôts', href: '/admin/depots', icon: AllProductsIcon, permissionName: 'depot' },
        { name: 'Transferts', href: '/admin/stock-transfers', icon: AllProductsIcon, permissionName: 'transfert' },
      ],
    },
    { name: 'Import', href: '/admin/import', icon: ImportIcon, permissionName: 'import_de_contacts' },
    { name: 'Codes Promo', href: '/admin/promocodes', icon: PromoCodeIcon, permissionName: 'code_promotionnel'},
    { name: 'Remises Roulette', href: '/admin/remises', icon: RouletteIcon, permissionName: 'remises', adminOnly: true },
    { name: 'Coupons Roulette', href: '/admin/coupons', icon: PromoCodeIcon, permissionName: 'coupons', adminOnly: true },
    { name: 'Clients', href: '/admin/clients', icon: UsersIcon, permissionName: 'clients', adminOnly: true },
    { name: 'Permissions', href: '/admin/permissions', icon: UsersIcon },
    { name: 'Paramètres', href: '/admin/settings', icon: SettingsIcon,permissionName: 'paramètres' },
    { name: 'Facebook Leads', href: '/admin/facebook-leads', icon: FaFacebook, adminOnly: true, permissionName: 'facebook_leads' },
  ];


  const filteredNavigation = isAuth? navigation:filterNavigationByPermissions(navigation);


  const isCurrentPath = (path) => {
    return location.pathname === path;
  };

  const isSubPathActive = (path) => {
    if (path === '#') {
      return filteredNavigation.find(item => item.hasDropdown)?.subItems.some(subItem => location.pathname.startsWith(subItem.href)) || false;
    }
    return location.pathname.startsWith(path);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen); // Toggle the sidebar visibility
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar */}
      <MobileMenu
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        navigation={filteredNavigation}
        isProductsExpanded={isProductsExpanded}
        isOrdersExpanded={isOrdersExpanded}
        isStockExpanded={isStockExpanded}
        isCaisseExpanded={isCaisseExpanded}
        handleProductsClick={handleProductsClick}
        handleOrdersClick={handleOrdersClick}
        handleStockClick={handleStockClick}
        handleCaisseClick={handleCaisseClick}
        isAchatsExpanded={isAchatsExpanded}
        handleAchatsClick={handleAchatsClick}
        isCurrentPath={isCurrentPath}
        isSubPathActive={isSubPathActive}
        user={user}
        getUserAvatar={getUserAvatar}
        handleLogout={handleLogout}
        ChevronDownIcon={ChevronDownIcon}
      />

      {/* Desktop sidebar */}
      {isSidebarOpen && (
        <Sidebar 
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          navigation={filteredNavigation}
          isProductsExpanded={isProductsExpanded}
          isOrdersExpanded={isOrdersExpanded}
          isStockExpanded={isStockExpanded}
          isCaisseExpanded={isCaisseExpanded}
          handleProductsClick={handleProductsClick}
          handleOrdersClick={handleOrdersClick}
          handleStockClick={handleStockClick}
          handleCaisseClick={handleCaisseClick}
          isAchatsExpanded={isAchatsExpanded}
          handleAchatsClick={handleAchatsClick}
          isCurrentPath={isCurrentPath}
          isSubPathActive={isSubPathActive}
          user={user}
          getUserAvatar={getUserAvatar}
          handleLogout={handleLogout}
          toggleSidebar={toggleSidebar}
          scrollbarStyles={scrollbarStyles}
          ChevronDownIcon={ChevronDownIcon}
        />
      )}

      {/* Small menu button when sidebar is collapsed */}
      {!isSidebarOpen && (
        <div
          className="hidden lg:flex mt-4 ml-4 inset-0 z-50 w-8 h-8 rounded-full bg-gradient-to-br from-[#1c5651]/60 to-[#0d2926]/60 items-center justify-center cursor-pointer hover:from-[#1c5651] hover:to-[#0d2926] transition-all duration-300 group shadow-lg"
          onClick={toggleSidebar}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-white group-hover:text-emerald-300 transition-colors duration-200"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation bar - mobile and desktop versions */}
        <TopBar
          isMobile={true}
          setIsSidebarOpen={setIsSidebarOpen}
          toggleSidebar={toggleSidebar}
          NotificationSystemComponent={NotificationSystem}
          notificationProps={{
            isNotificationsOpen,
            setIsNotificationsOpen,
            notifications,
            isLoading,
            error,
            unreadCount,
            markAllAsRead,
            markAsRead,
            formatPhoneNumber,
            notificationsRef
          }}
        />

        <TopBar
          isMobile={false}
          setIsSidebarOpen={setIsSidebarOpen}
          toggleSidebar={toggleSidebar}
          NotificationSystemComponent={NotificationSystem}
          notificationProps={{
            isNotificationsOpen,
            setIsNotificationsOpen,
            notifications,
            isLoading,
            error,
            unreadCount,
            markAllAsRead,
            markAsRead, 
            formatPhoneNumber,
            notificationsRef
          }}
        />

        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;